/**
 * useFlowWorkflow - Orchestrates Flow mode state and data
 * Combines journey phases, entity instances, and AI suggestions
 */

import { useMemo, useCallback } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { useJourneyStore } from '@/stores/journeyStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useEntityTree } from './useEntityTree'
import type { JourneyPhaseWithMode, EntityDomain, PhaseEntity } from '@ispot/shared'
import type { EntityTreeInstance, EntityTreeClassGroup } from '@/lib/api/client'

// ============================================================================
// TYPES
// ============================================================================

export interface PhaseEntityCard {
  classUri: string
  className: string
  domain: EntityDomain
  description?: string
  isRequired: boolean
  minCount: number
  currentCount: number
  instances: EntityTreeInstance[]
}

export interface UseFlowWorkflowReturn {
  // Session
  accountId: string | null
  projectId: string | null

  // Phase data
  phases: JourneyPhaseWithMode[]
  currentPhase: JourneyPhaseWithMode | null
  currentPhaseIndex: number
  totalPhases: number
  phaseProgress: Record<string, number>

  // Entity data for current phase
  phaseEntityCards: PhaseEntityCard[]

  // Domain filtering
  domainFilter: { market: boolean; business: boolean; solution: boolean }

  // Loading states
  isLoading: boolean
  isJourneyLoading: boolean
  isEntitiesLoading: boolean

  // Actions
  setCurrentPhase: (phaseId: string) => void
  goToNextPhase: () => void
  goToPreviousPhase: () => void
  canGoNext: boolean
  canGoPrevious: boolean
}

// ============================================================================
// HOOK
// ============================================================================

export function useFlowWorkflow(): UseFlowWorkflowReturn {
  const { accountId, projectId } = useSessionStore()

  // Journey state
  const {
    framework,
    journey,
    isLoading: isJourneyLoading,
    getActivePhases,
    getPhaseProgress,
  } = useJourneyStore()

  // Workspace state for current phase
  const { currentPhaseId, setCurrentPhaseId } = useWorkspaceStore()

  // Entity tree data
  const { byDomain, isLoading: isEntitiesLoading } = useEntityTree({ mode: 'free' })

  // Get active phases from journey
  const phases = useMemo(() => getActivePhases(), [getActivePhases, framework, journey])

  // Find current phase
  const currentPhase = useMemo(() => {
    if (!currentPhaseId || phases.length === 0) {
      return phases[0] || null
    }
    return phases.find(p => p.id === currentPhaseId) || phases[0]
  }, [currentPhaseId, phases])

  const currentPhaseIndex = useMemo(() => {
    if (!currentPhase) return 0
    return phases.findIndex(p => p.id === currentPhase.id)
  }, [currentPhase, phases])

  // Build phase progress map
  const phaseProgress = useMemo(() => {
    const progress: Record<string, number> = {}
    for (const phase of phases) {
      progress[phase.id] = getPhaseProgress(phase.id)
    }
    return progress
  }, [phases, getPhaseProgress, journey])

  // Build entity cards for current phase
  const phaseEntityCards = useMemo((): PhaseEntityCard[] => {
    if (!currentPhase?.entities) return []

    // Create a map of normalized className -> instances
    // Entity tree uses full URI (http://...#CustomerSegment), phase uses prefixed (market:CustomerSegment)
    // We normalize both to just the className for matching
    const instancesByClassName = new Map<string, EntityTreeInstance[]>()

    // Flatten all domain groups into the map, keyed by className
    const allGroups: EntityTreeClassGroup[] = [
      ...(byDomain.market || []),
      ...(byDomain.business || []),
      ...(byDomain.solution || []),
    ]

    for (const group of allGroups) {
      // Use className as key (normalized form)
      instancesByClassName.set(group.className, group.instances)
    }

    // Build cards from phase entities
    return currentPhase.entities.map((entity: PhaseEntity) => {
      // Derive className from classUri to match against entity tree
      const className = getClassNameFromUri(entity.classUri)

      // Look up instances using normalized className
      const instances = instancesByClassName.get(className) || []
      // Count all non-rejected instances (pending + confirmed)
      // User-added entities should count immediately, not just after confirmation
      const activeInstances = instances.filter(i => i.status !== 'rejected')

      // Derive domain from classUri (e.g., "market:CustomerSegment")
      const domain = getDomainFromUri(entity.classUri)

      return {
        classUri: entity.classUri,
        className: className || entity.label,
        domain,
        description: getEntityDescription(className || entity.label),
        isRequired: entity.isRequired || (entity.minCount || 0) > 0,
        minCount: entity.minCount || 0,
        currentCount: activeInstances.length,
        instances: instances,
      }
    })
  }, [currentPhase, byDomain])

  // Navigation actions
  const setCurrentPhase = useCallback((phaseId: string) => {
    setCurrentPhaseId(phaseId)
  }, [setCurrentPhaseId])

  const goToNextPhase = useCallback(() => {
    if (currentPhaseIndex < phases.length - 1) {
      setCurrentPhaseId(phases[currentPhaseIndex + 1].id)
    }
  }, [currentPhaseIndex, phases, setCurrentPhaseId])

  const goToPreviousPhase = useCallback(() => {
    if (currentPhaseIndex > 0) {
      setCurrentPhaseId(phases[currentPhaseIndex - 1].id)
    }
  }, [currentPhaseIndex, phases, setCurrentPhaseId])

  const canGoNext = currentPhaseIndex < phases.length - 1
  const canGoPrevious = currentPhaseIndex > 0

  return {
    // Session
    accountId,
    projectId,

    // Phase data
    phases,
    currentPhase,
    currentPhaseIndex,
    totalPhases: phases.length,
    phaseProgress,

    // Entity data
    phaseEntityCards,

    // Domain filter (all enabled for now)
    domainFilter: { market: true, business: true, solution: true },

    // Loading states
    isLoading: isJourneyLoading || isEntitiesLoading,
    isJourneyLoading,
    isEntitiesLoading,

    // Actions
    setCurrentPhase,
    goToNextPhase,
    goToPreviousPhase,
    canGoNext,
    canGoPrevious,
  }
}

// ============================================================================
// HELPERS
// ============================================================================

function getEntityDescription(className: string): string {
  const descriptions: Record<string, string> = {
    'Industry': 'The economic sector you are operating in',
    'CustomerSegment': 'A homogeneous group of customers with similar needs',
    'Persona': 'An archetypal user or buyer representation',
    'CustomerPain': 'A problem or frustration your customers experience',
    'Competitor': 'A company offering alternative solutions',
    'Evidence': 'Data or proof supporting your assumptions',
    'Trend': 'A market or industry direction',
    'ValueProposition': 'The unique value you deliver to customers',
    'Feature': 'A capability or characteristic of your product',
    'Requirement': 'A specific need the solution must fulfill',
    'Channel': 'A path to reach and serve customers',
    'RevenueStream': 'How you generate income from customers',
    'Partnership': 'Strategic relationships that enable your business',
  }
  return descriptions[className] || `${className} entities`
}

/**
 * Derive domain from classUri
 * Supports both formats:
 * - Full URI: "http://ispot.io/ontology/market#CustomerSegment" -> "market"
 * - Prefixed: "market:CustomerSegment" -> "market"
 */
function getDomainFromUri(uri: string): EntityDomain {
  // Check for prefixed format first (e.g., "market:CustomerSegment")
  if (uri.startsWith('market:')) return 'market'
  if (uri.startsWith('business:')) return 'business'
  if (uri.startsWith('solution:')) return 'solution'

  // Check for full URI format
  if (uri.includes('/market#')) return 'market'
  if (uri.includes('/business#')) return 'business'
  if (uri.includes('/solution#')) return 'solution'

  // Default to market if unknown
  return 'market'
}

/**
 * Derive className from classUri
 * Supports both formats:
 * - Full URI: "http://ispot.io/ontology/market#CustomerSegment" -> "CustomerSegment"
 * - Prefixed: "market:CustomerSegment" -> "CustomerSegment"
 */
function getClassNameFromUri(uri: string): string {
  // Check for prefixed format (e.g., "market:CustomerSegment")
  const colonIndex = uri.indexOf(':')
  if (colonIndex !== -1 && !uri.includes('/')) {
    return uri.substring(colonIndex + 1)
  }

  // Check for hash format (e.g., "http://...#CustomerSegment")
  const hashIndex = uri.lastIndexOf('#')
  if (hashIndex !== -1) {
    return uri.substring(hashIndex + 1)
  }

  // Fallback: try last path segment
  const slashIndex = uri.lastIndexOf('/')
  if (slashIndex !== -1) {
    return uri.substring(slashIndex + 1)
  }

  return uri
}

export default useFlowWorkflow
